import { useEffect, useState } from "react";
import { History, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { formatGal, formatToEighths } from "@/lib/tank";
import { StickPhoto } from "@/components/StickPhoto";

const STATUS = { safe: "#34D399", caution: "#F59E0B", critical: "#F87171" };

const ago = (iso, t) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return t("{n}m ago", { n: Math.max(1, m) });
  if (m < 1440) return t("{n}h ago", { n: Math.floor(m / 60) });
  return t("{n}d ago", { n: Math.floor(m / 1440) });
};

// Drops for the active saved tank, plus how close past predictions were to the real stick.
export function useTankDrops(s, enabled = true, chartMode = null) {
  const [drops, setDrops] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.getDrops().then(setDrops).catch(() => setDrops([]));
    load();
    window.addEventListener("drops-changed", load);
    return () => window.removeEventListener("drops-changed", load);
  }, [enabled]);
  const mine = (drops || []).filter((d) => d.siteId === s.activeSiteId && d.tankLabel === s.activeLabel);
  const same = (d) => chartMode === null || (!!d.usedChart === chartMode && d.capacity === s.capacity && d.diameter === s.diameter && d.ends === s.ends);
  const checked = mine.filter((d) => d.actualInches != null && same(d));
  const errs = checked.map((d) => Math.abs(d.actualInches - d.finalInches));
  return { drops, mine, record: { n: checked.length, max: errs.length ? Math.max(...errs) : 0 } };
}

function ActualEntry({ d, t }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(d.finalInches.toFixed(3).replace(/\.?0+$/, ""));
  const [err, setErr] = useState("");
  const save = async () => {
    const v = parseFloat(val);
    if (!(v >= 0 && v <= 240)) { setErr(t("Enter inches between 0 and 240.")); return; }
    try { await api.setActual(d.id, v); setErr(""); window.dispatchEvent(new Event("drops-changed")); setOpen(false); }
    catch (e) { setErr(e.response ? t("Couldn't save.") : t("No signal. Try again when you have signal.")); }
  };
  if (d.actualInches != null) {
    const off = Math.abs(d.actualInches - d.finalInches);
    return <span className={`th-check ${off <= 0.5 ? "th-good" : "th-bad"}`} data-testid={`tank-actual-${d.id}`}>
      {t("Real {a} · off {o}", { a: formatToEighths(d.actualInches), o: formatToEighths(off) })}</span>;
  }
  if (!open) return <button type="button" className="load-btn" data-testid={`tank-check-${d.id}`} onClick={() => setOpen(true)}>{t("Enter real stick")}</button>;
  return (
    <span className="th-entry">
      <input className="text-input th-input" type="number" inputMode="decimal" step="0.125" min="0" max="240" value={val}
        data-testid={`tank-actual-input-${d.id}`} onChange={(e) => setVal(e.target.value)} />
      <button type="button" className="load-btn load-btn-active" data-testid={`tank-actual-save-${d.id}`} onClick={save}><Check size={14} /></button>
      {err && <span className="form-error" data-testid={`tank-actual-error-${d.id}`}>{err}</span>}
      <StickPhoto testid={`tank-actual-${d.id}`} max={240} onValue={(v) => setVal(String(v))} />
    </span>
  );
}

export function TankHistory({ s }) {
  const t = useT();
  const { drops, mine } = useTankDrops(s);
  const rows = mine.slice(0, 3);
  return (
    <div className="card tank-history" data-testid="tank-history">
      <div className="label"><History size={12} /> {t("Last drops in this tank")}</div>
      {drops === null ? <div className="card-sub">…</div> : rows.length === 0 ? (
        <div className="card-sub" data-testid="tank-history-empty">{t("No drops logged for this tank yet.")}</div>
      ) : rows.map((d) => (
        <div key={d.id} className="th-row" data-testid={`tank-history-${d.id}`} style={{ borderLeftColor: STATUS[d.status] }}>
          <div className="th-line">
            <span className="mono">{formatToEighths(d.startInches)} → {formatToEighths(d.finalInches)}</span>
            <span className="mono">+{formatGal(d.dropGallons)} gal</span>
            <span className="card-sub">{ago(d.at, t)}</span>
          </div>
          <ActualEntry d={d} t={t} />
        </div>
      ))}
      {rows.length > 0 && <div className="field-hint">{t("After a drop, stick the tank and enter the real reading. The app shows how close it was.")}</div>}
    </div>
  );
}
